import DPO from 'dpo8core';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { inject } from '@app/providers/QAU.ContextProvider';
import { stateArtworkPreview, setArtworkPreviewZoom, setLastClickCoordinates } from '@app/actions/whiteInkOptionsActions';

import './WorkingArea.scss';

const WorkingArea = ({
  flow,
  context,
  artworkPreview,
  artworkPreviewExists,
  includesWhiteUnderprint,
  stateArtworkPreview,
  setArtworkPreviewZoom,
  setLastClickCoordinates,
  whiteInkOption,
  touchUpMode,
  isDarkMode,
}) => {
  const workingAreaElRef = useRef();

  const getPanelForPreview = () => {
    const { project: { masterPanels: [masterPanel] } } = flow;
    const [childPanel] = context.selector.selectChildPanels(masterPanel) || [];

    return childPanel;
  };

  const createArtworkPreview = useCallback(() => {
    const panel = getPanelForPreview();
    const preview = new DPO.render.ArtworkPreview(
      workingAreaElRef.current,
      context,
      { id: 'qau-artwork-preview' }
    );

    preview.panel = panel;
    preview.on('changed:zoom', onZoomChanged, this);
    preview.setZoomLevel(preview.calculatePreferredZoom());

    return preview;
  }, []);

  const onZoomChanged = useCallback((sender, zoom) => {
      setArtworkPreviewZoom(zoom);
    },
    [setArtworkPreviewZoom],
  );

  useEffect(() => {
    const { zoom = 1 } = artworkPreview || {};
    setArtworkPreviewZoom(zoom);
  }, [artworkPreview, setArtworkPreviewZoom]);

  useEffect(() => {
    !artworkPreviewExists && stateArtworkPreview(createArtworkPreview());
  }, [createArtworkPreview, artworkPreviewExists, stateArtworkPreview]);

  useEffect(() => {
    if (!artworkPreview) return;

    const svgRootEl = artworkPreview.root.node;
    const pickCoordinatesListener = artworkPreview.pickCoordinatesHandler.bind(artworkPreview);
    const isSelectWhiteToRemove = whiteInkOption === 'selectWhiteToRemove';
    const activateCoordinatesPicker = includesWhiteUnderprint && (touchUpMode !== null || isSelectWhiteToRemove);

    svgRootEl.classList.toggle('bucket', activateCoordinatesPicker);

    if (activateCoordinatesPicker) {
      svgRootEl.addEventListener('click', pickCoordinatesListener);
      artworkPreview.on('coordinates:picked', setLastClickCoordinates);
    }

    return () => {
      artworkPreview.un('coordinates:picked', setLastClickCoordinates);
      svgRootEl.removeEventListener('click', pickCoordinatesListener);
      svgRootEl.classList.toggle('bucket', false);
    };
  }, [whiteInkOption, touchUpMode, includesWhiteUnderprint]);

  return <div
    ref={workingAreaElRef}
    className={classnames({'dark-mode': isDarkMode})}
    id="qau-white-options-working-area"
  >
  </div>;
};

const mapStateToProps = ({
  whiteInkOptions: {
    artworkPreview,
    artworkPreviewZoom,
    includesWhiteUnderprint,
    whiteInkOption,
    isDarkMode,
    advancedMode: {
      touchUpMode,
    }
  },
}, { context }) => ({
  context,
  artworkPreview,
  artworkPreviewExists: !!artworkPreview,
  artworkPreviewZoom,
  includesWhiteUnderprint,
  touchUpMode,
  whiteInkOption,
  isDarkMode,
});

const mapDispatchToProps = {
  stateArtworkPreview,
  setArtworkPreviewZoom,
  setLastClickCoordinates,
};

WorkingArea.propTypes = {
  flow: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  artworkPreview: PropTypes.object,
  whiteInkOption: PropTypes.string,
  artworkPreviewExists: PropTypes.bool.isRequired,
  includesWhiteUnderprint: PropTypes.bool.isRequired,
  touchUpMode: PropTypes.string,
  isDarkMode: PropTypes.bool.isRequired,
  stateArtworkPreview: PropTypes.func.isRequired,
  setArtworkPreviewZoom: PropTypes.func.isRequired,
  setLastClickCoordinates: PropTypes.func.isRequired,
};

export default inject('flow', 'context')(connect(mapStateToProps, mapDispatchToProps)(WorkingArea));
